import { useSyncExternalStore } from 'react';

// Tiny module-level pub/sub carrying the canvas's currently-hovered note
// link into React. Canvas.jsx's mousemove closures own the write side
// (imperative, once per hover-zone TRANSITION — not once per mousemove
// tick, same change-gating convention every other hoveredXRef in Canvas.jsx
// already follows); NoteLinkPreview reads it via useHoveredNoteLink().
//
// Why not the two mechanisms already in this codebase:
//  - Redux (`searchHoveredColumn`, `SET_SEARCH_HOVERED_COLUMN`) is the
//    closest existing precedent for "hover state shared between the canvas
//    and a React sibling", but it runs the OPPOSITE direction: a React list
//    item's onMouseEnter dispatches into redux, and Canvas reads it back out
//    to paint a highlight. Routing a mousemove-driven value the other way
//    through the global reducer would mean every store-connected component
//    re-rendering on each hover transition, for state nothing outside this
//    one popover cares about (never persisted, never collaborative, never
//    undoable).
//  - Yjs awareness (`useAwarenessMembers`, see src/lib/yjs/hooks) is for
//    state that must reach OTHER collaborators (remote cursors, selection).
//    A local hover preview has no business on the wire.
// This store mirrors the shape of those hooks (subscribe/getSnapshot, fed
// through useSyncExternalStore) without either dependency.
//
// Grace/hide semantics (spec 8b): the popover must survive the pointer
// crossing the gap between the link's canvas hit zone and the popover's own
// DOM — so leaving either schedules a 150ms hide, and entering either
// cancels a pending one. Certain events must hide it INSTANTLY instead
// (pan/zoom start, any canvas mousedown, the note entering edit mode, a
// view-mode change) — callers use hideNoteLinkPreviewImmediately for those.
const HIDE_GRACE_MS = 150;

let state = null; // null | { href, x, y, actions }
const listeners = new Set();
let hideTimer = null;

const notify = () => listeners.forEach((listener) => listener());

const clearHideTimer = () => {
    if (hideTimer == null) return;
    clearTimeout(hideTimer);
    hideTimer = null;
};

const setHoveredNoteLink = (next) => {
    if (state === next) return;
    state = next;
    notify();
};

export const getHoveredNoteLink = () => state;

export const subscribeHoveredNoteLink = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

// Canvas mousemove: pointer entered a (different) link zone, or is still
// over the same one — cancels any pending grace-hide and shows/updates it.
export const noteLinkZoneEntered = (value) => {
    clearHideTimer();
    setHoveredNoteLink(value);
};

// Canvas mousemove: pointer left every link zone. Grace period, not
// immediate — lets the user travel the gap down into the popover itself.
export const noteLinkZoneLeft = () => {
    clearHideTimer();
    hideTimer = setTimeout(() => {
        hideTimer = null;
        setHoveredNoteLink(null);
    }, HIDE_GRACE_MS);
};

// NoteLinkPreview's own onMouseEnter/onMouseLeave — same grace contract as
// the zone above, from the popover's side of the gap.
export const noteLinkPopoverEntered = () => clearHideTimer();
export const noteLinkPopoverLeft = () => { noteLinkZoneLeft(); };

// Pan/zoom start, canvas mousedown, note-editor open, view-mode change: no
// grace, no gap to protect — the popover's anchor is about to be invalid.
export const hideNoteLinkPreviewImmediately = () => {
    clearHideTimer();
    setHoveredNoteLink(null);
};

// NoteLinkPreview's read side.
export const useHoveredNoteLink = () =>
    useSyncExternalStore(subscribeHoveredNoteLink, getHoveredNoteLink, () => null);
